import React, {useContext, useRef} from 'react';
import {Card} from "../../../../../../components/card/card.jsx";
import InputForm from "../../../../../../components/input/input-form.jsx";
import {CitizenContext} from "../context/citizen-context.jsx";
import {MaskDefault} from "../../../../../../utils/mask.js";
import InputDateTime from "../../../../../../components/input/input-datetime.jsx";
import SelectComponent from "../../../../../../components/select/select-component.jsx";
import optionsUtils from "../../../../../../utils/options-utils.js";
import InputTextarea from "../../../../../../components/input/input-textarea.jsx";
import {getCities, getCountries} from "../../../../../../utils/location-utils.js";
import InputSwitch from "../../../../../../components/input/input-switch.jsx";
import Button from "../../../../../../components/button/button.jsx";
import citizenService from "../../../../../../services/web/citizen-service.js";
import projectUtils from "../../../../../../utils/project-utils.js";
import CitizenCredentialTable from "./citizen-credential-table.jsx";
import {format} from "date-fns";
import Alert from "../../../../../../components/alert/alert.jsx";
import individualService from "../../../../../../services/web/individual-service.js";

const CitizenGeneral = () => {
    const {id, citizen, setCitizen, fetchData} = useContext(CitizenContext);

    const handleInput = ({target: {id, value}}) => setCitizen(prev => ({...prev, [id]: id === "cnpj" ? value?.toUpperCase() : value}));

    const isCpf = citizen.type === "CPF";

    const cpfOnFocusRef = useRef(null);

    const handleCpfBlur = ({target: {value}}) => {
        if (value?.replace(/\D/g, '') === cpfOnFocusRef.current?.replace(/\D/g, '')) return;

        searchIndividualByCpf(value);
    }

    const searchIndividualByCpf = (cpf) => {
        const cpfClear = cpf?.replace(/\D/g, '');
        if (!cpfClear || cpfClear.length < 11) return;

        individualService.get(cpfClear).then(({data}) => {
            if (!data) return;

            const dataParse = JSON.parse(data.data);

            setCitizen(prev => ({
                ...prev,
                name: dataParse.name,
                socialName: dataParse.socialName,
                dtBirth: dataParse.dtBirthRaw,
                nationality: dataParse.nationality,
                cityBirth: dataParse.cityOfBirth,
                sex: buildSex(dataParse.sex)
            }));
        });
    }

    const buildSex = (sex) => {
        if (!sex) return null
        if (sex === "FEMININO") return "FEMALE"
        if (sex === "MASCULINO") return "MALE"
    }

    const changePassword = () => {
        if (citizen?.hasPassword) {
            projectUtils.swalMessage(
                "warning",
                "Atenção",
                "Trocar a senha de acesso ao app Sentry SOS? Essa operação não pode ser desfeita.",
                "Sim",
                true,
                "Não",
                ({isConfirmed}) => {
                    if (isConfirmed) changePasswordPersist();
                }
            )

            return;
        }

        changePasswordPersist();
    }

    const changePasswordPersist = () => {
        citizenService.changePassword(id)
            .then(data => {
                projectUtils.swalMessage(
                    "warning",
                    "Nova senha:",
                    data.password,
                    "Ok",
                    false,
                    "",
                    undefined,
                    false,
                )

                setCitizen(prev => ({...prev, hasPassword: true}))
            })
            .catch(err => {
                console.error(err);
                projectUtils.swalErrorMessage("", "Não foi possível prosseguir.", "Ok")
            });
    }

    return (
        <article className={"space-y-2"}>
            <Card>
                <Card.Header>
                    <Card.Title>Identificação {citizen.type === "CPF" ? "do protegido" : "da empresa" }</Card.Title>
                </Card.Header>
                <Card.Body className={"space-y-4"}>
                    {
                        citizen.id &&
                        <section className={"grid grid-cols-12 gap-4"}>
                            <div className={"col-span-4 flex flex-row gap-4 text-[16px] items-center"}>
                                <InputSwitch
                                    id="isMain"
                                    checked={citizen.status === "ACTIVATED"}
                                    onChange={(checked) => setCitizen(prev => ({...prev, status: checked ? "ACTIVATED" : "INACTIVATED"}))}
                                    title={citizen.status === "ACTIVATED" ? "Ativo" : "Inativo"}
                                    label={"Status"}
                                />

                                <InputSwitch
                                    id="noApp"
                                    checked={!citizen.noApp}
                                    onChange={(checked) => setCitizen(prev => ({...prev, noApp: !checked}))}
                                    label={"App Sentry SOS"}
                                />
                            </div>
                        </section>
                    }

                    <section className={"grid grid-cols-12 gap-4 w-full"}>
                        <SelectComponent
                            classNameWrapper={"col-span-6"}
                            value={citizen.protectionProgram ?? []}
                            options={fetchData.programs}
                            label={"Programa *"}
                            isMulti
                            onChange={(value) => setCitizen(prev => ({...prev, protectionProgram: value, protectiveMeasure: value?.some(p => p.protectiveMeasure) ?? false}))}
                        />

                        <div className={"col-span-6"}></div>

                        <InputForm
                            id={"name"}
                            label={isCpf ? "Nome *" : "Razão social *"}
                            classNameWrapper={"col-span-6"}
                            value={citizen.name}
                            onChange={handleInput}
                        />

                        {
                            isCpf ?
                                <InputForm
                                    id={"socialName"}
                                    label={"Nome social"}
                                    classNameWrapper={"col-span-3"}
                                    value={citizen.socialName}
                                    onChange={handleInput}
                                />
                                :
                                <InputForm
                                    id={"aliasName"}
                                    label={"Nome fantasia"}
                                    classNameWrapper={"col-span-3"}
                                    value={citizen.aliasName}
                                    onChange={handleInput}
                                />
                        }

                        {
                            isCpf ?
                                <InputForm
                                    id={"cpf"}
                                    label={"CPF *"}
                                    classNameWrapper={"col-span-3"}
                                    value={citizen.cpf}
                                    onChange={handleInput}
                                    onFocus={({target: {value}}) => cpfOnFocusRef.current = value}
                                    onBlur={handleCpfBlur}
                                    mask={MaskDefault.cpf}
                                />
                                :
                                <InputForm
                                    id={"cnpj"}
                                    label={"CNPJ *"}
                                    classNameWrapper={"col-span-3"}
                                    value={citizen.cnpj}
                                    onChange={handleInput}
                                    mask={MaskDefault.cnpj}
                                />
                        }

                        {
                            isCpf &&
                            <>
                                <InputDateTime
                                    id={"dtBirth"}
                                    label={"Nascimento *"}
                                    dateFormat="L"
                                    classNameWrapper={"col-span-2"}
                                    selected={citizen.dtBirth}
                                    onChange={(date) => {
                                        const dateFormated = date ? format(date, "yyyy-MM-dd") : null
                                        if (citizen.dtBirth === dateFormated) return

                                        setCitizen(prev => ({...prev, dtBirth: date}))
                                    }}
                                />

                                <SelectComponent
                                    classNameWrapper={"col-span-2"}
                                    value={citizen.sex}
                                    options={optionsUtils.sex}
                                    label={"Sexo *"}
                                    onChange={(value) => setCitizen(prev => ({...prev, sex: value?.value}))}
                                />
                            </>
                        }

                        <InputForm
                            id={"phone"}
                            label={"Telefone"}
                            classNameWrapper={isCpf ? "col-span-3" : "col-span-6"}
                            value={citizen.phone}
                            onChange={handleInput}
                            mask={MaskDefault.phone}
                        />

                        <InputForm
                            id={"email"}
                            label={"E-mail "}
                            classNameWrapper={isCpf ? "col-span-5" : "col-span-6"}
                            value={citizen.email}
                            onChange={handleInput}
                        />

                        <InputForm
                            id={"identificationNumber"}
                            label={"Número de  identificação"}
                            classNameWrapper={"col-span-4"}
                            value={citizen.identificationNumber}
                            onChange={handleInput}
                        />

                        {
                            id &&
                            <Button className={"!w-max"} onClick={changePassword}>
                                Trocar senha
                            </Button>
                        }
                    </section>
                </Card.Body>
            </Card>

            {
                citizen.protectiveMeasure &&
                <Card>
                    <Card.Header>
                        <Card.Title>Dados adicionais</Card.Title>
                    </Card.Header>
                    <Card.Body>
                        <section className={"grid grid-cols-12 gap-4 w-full"}>
                            <InputForm
                                id={"childCount"}
                                label={"Número de filhos"}
                                classNameWrapper={"col-span-4"}
                                value={citizen.childCount}
                                onChange={handleInput}
                                type={"number"}
                            />

                            <SelectComponent
                                classNameWrapper={"col-span-4"}
                                value={citizen.educationId}
                                options={fetchData.educations}
                                label={"Escolaridade"}
                                onChange={(value) => setCitizen(prev => ({...prev, educationId: value?.value, education: value?.label}))}
                            />

                            <SelectComponent
                                classNameWrapper={"col-span-4"}
                                loadOptions={getCountries}
                                value={optionsUtils.getValue(citizen.nationality)}
                                isAsync={true}
                                label="Nacionalidade"
                                onChange={(value) => setCitizen(prev => ({...prev, nationality: value?.value}))}
                                cacheOptions
                                defaultOptions={[]}
                            />

                            <SelectComponent
                                classNameWrapper={"col-span-4"}
                                value={citizen.raceEthnicityId}
                                options={fetchData.races}
                                label={"Raça ou etnia"}
                                onChange={(value) => setCitizen(prev => ({...prev, raceEthnicityId: value?.value, raceEthnicity: value?.label}))}
                            />

                            <SelectComponent
                                classNameWrapper={"col-span-4"}
                                value={citizen.religionId}
                                options={fetchData.religions}
                                label={"Religião"}
                                onChange={(value) => setCitizen(prev => ({...prev, religionId: value?.value, religion: value?.label}))}
                            />

                            <SelectComponent
                                classNameWrapper={"col-span-4"}
                                loadOptions={getCities}
                                value={optionsUtils.getValue(citizen.cityBirth)}
                                isAsync={true}
                                label="Naturalidade"
                                onChange={(value) => setCitizen(prev => ({...prev, cityBirth: value?.value}))}
                                cacheOptions
                                defaultOptions={[]}
                            />
                        </section>
                    </Card.Body>
                </Card>
            }

            {
                id &&
                <Card>
                    <Card.Header>
                        <Card.Title>Funcionamento do aplicativo</Card.Title>
                    </Card.Header>

                    <Card.Body>
                        <div className={"h-[300px]"}>
                            <CitizenCredentialTable
                                credentials={citizen?.credentials}
                                setCitizen={setCitizen}
                                remove={true}
                            />
                        </div>
                    </Card.Body>
                </Card>
            }

            <Card>
                <Card.Header>
                    <Card.Title>Observação</Card.Title>
                </Card.Header>
                <Card.Body className={"space-y-4"}>
                    <InputTextarea
                        id={"observation"}
                        textAreaClass={"h-20"}
                        label={"Observação"}
                        value={citizen?.observation ?? ""}
                        onChange={handleInput}
                    />
                </Card.Body>
            </Card>

            {
                isCpf &&
                <Alert target={"warning"}>
                    Ao realizar o cadastro, será gerada uma senha provisória usando a data de nascimento sem formatação. Oriente o protegido a alterá-la no primeiro acesso.
                    <br />
                    Exemplo: para a data de nascimento <b>15/05/1980</b>, a senha gerada será <b>15051980</b>.
                </Alert>
            }
        </article>
    );
};

export default CitizenGeneral;
